import { useIntl } from 'react-intl';
import classnames from 'classnames';

import { FlexRow, FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './messages';
import { RateAmount } from './rate-amount';
import style from './rate.module.less';
import { useFulfillmentPlannerContext } from '../../../../fulfillment-planner/context';
import { CostReviewLink, getShouldDisableCostReview } from '../../../../fulfillment-planner/cost-review-link';
import { useIsTikTokShippingOrder } from '../../../../fulfillment-planner/hooks/use-is-tiktok-shipping-order';
import RateRefresher from '../../../../fulfillment-planner/rate/rate-refresher';
import RateProvidedLabel from '../../../../fulfillment-planner/rate-consumer/rate-provided-label';
import { OrderRateWarningTooltip } from '../../../../fulfillment-planner/rate-consumer/rate-warning-tooltip/order-rate-warning-tooltip';
import ThirdPartyBillingTooltip from '../../../../fulfillment-planner/rate-consumer/third-party-billing-tooltip';

import buttonTextStyle from 'ui-components/components/button/button-text.module.less';

/**
 * @param {object} props
 * @param {string[]} [props.costReviewFulfillmentPlanIds]
 * @param {boolean} [props.hasProvidedRate]
 * @param {boolean} [props.isThirdPartyBilling]
 * @param {CurrencyPropType} [props.rate]
 * @param {boolean} [props.hideRateRefresher]
 * @param {keyof typeof TextType} [props.rateAmountTextType]
 * @param {string} [props.refreshButtonClassName]
 * @param {string} [props.refreshIconClassName]
 * @returns {React.JSX.Element}
 */
const Rate = ({
  costReviewFulfillmentPlanIds,
  hasProvidedRate = false,
  isThirdPartyBilling = false,
  rate,
  hideRateRefresher = false,
  rateAmountTextType = TextType.H2,
  refreshButtonClassName = '',
  refreshIconClassName = '',
}) => {
  const { formatMessage } = useIntl();

  const rateToShow = isThirdPartyBilling ? undefined : rate;

  const { fulfillmentPlanId, isRating, rateErrors } = useFulfillmentPlannerContext();
  const isTikTokShipping = useIsTikTokShippingOrder(fulfillmentPlanId);

  const isCostReviewDisabled = getShouldDisableCostReview({
    isRating,
    hasRateErrors: Boolean(rateErrors?.length),
    hasRate: Boolean(rateToShow?.value),
    hasProvidedRate: hasProvidedRate && !isTikTokShipping,
  });

  return (
    <FlexColumn className={style['rate-and-cost-review-container']}>
      <FlexRow className={style['rate-container']}>
        <OrderRateWarningTooltip />
        {hasProvidedRate && !isTikTokShipping ? (
          <Text className={style['rate-amount']} textType={rateAmountTextType} value={<RateProvidedLabel />} />
        ) : (
          <>
            {isThirdPartyBilling ? (
              <ThirdPartyBillingTooltip
                iconClassName={style['third-party-billing-tooltip-icon']}
                iconContainerClassName={style['third-party-billing-tooltip-icon-container']}
              />
            ) : null}
            <Text
              aria-description={formatMessage(messages.rateAriaDescription)}
              className={style['rate-amount']}
              textType={rateAmountTextType}
              value={<RateAmount rate={rateToShow} />}
            />
            {!hideRateRefresher && (
              <FlexColumn>
                <RateRefresher
                  buttonClassName={classnames(style['refresh-rate-button'], refreshButtonClassName)}
                  className={classnames(style['refresh-rate-button-icon'], refreshIconClassName)}
                />
              </FlexColumn>
            )}
          </>
        )}
      </FlexRow>
      {costReviewFulfillmentPlanIds?.length > 0 && (
        <CostReviewLink
          buttonClassName={classnames(style['cost-review-button'], buttonTextStyle['button-text'])}
          className={style['cost-review-container']}
          fulfillmentPlanIds={costReviewFulfillmentPlanIds}
          linkDisabled={isCostReviewDisabled}
        />
      )}
    </FlexColumn>
  );
};

export default Rate;
